import { Box, CardContent, Avatar, Typography, Button, IconButton, CircularProgress } from '@mui/material';
import { Email, Phone, Edit, PhotoCamera } from '@mui/icons-material';

import { Card } from '@mui/material';
import { FC, useState, useRef, useEffect } from 'react';

import { useAuth } from '@carcareers/hooks';
import {
  useJobseekerProfileQuery,
  useUploadProfilePictureMutation,
} from '@carcareers/store';
import { CardLoader, JobseekerProfileModal } from '@carcareers/components';

export const ProfileCard: FC = () => {
  const { session } = useAuth();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [avatarHovered, setAvatarHovered] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: profile, isLoading } = useJobseekerProfileQuery(session?.profile?.id ?? 0, {
    skip: !session?.profile?.id,
  });

  const [uploadProfilePicture, { isLoading: isUploading }] = useUploadProfilePictureMutation();

  const handleOpenEdit = () => setIsEditOpen(true);
  const handleCloseEdit = () => setIsEditOpen(false);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && session?.profile?.id) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        await uploadProfilePicture({
          formData,
          jobseekerId: session.profile.id,
        }).unwrap();
      } catch (error) {
        console.error('Failed to upload profile picture:', error);
      }
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: '16px !important' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              grow: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: { xs: 2, md: 4 } }}>
              {isLoading ? (
                <CardLoader sx={{ flex: 1, width: '100%' }} />
              ) : profile ? (
                <>
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'inline-block',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={() => setAvatarHovered(true)}
                    onMouseLeave={() => setAvatarHovered(false)}
                    onClick={handleAvatarClick}
                  >
                    <Avatar
                      sx={{
                        width: { xs: 60, md: 80 },
                        height: { xs: 60, md: 80 },
                        flexShrink: 0,
                        transition: 'filter 0.2s ease-in-out',
                        filter: avatarHovered ? 'brightness(0.5)' : 'none',
                      }}
                      src={isUploading ? undefined : profile.profilePictureUrl || undefined}
                    >
                      {isUploading ? (
                        <CircularProgress size={24} />
                      ) : (
                        <>
                          {profile.firstName?.[0]}
                          {profile.lastName?.[0]}
                        </>
                      )}
                    </Avatar>
                    {avatarHovered && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -45%)',
                          pointerEvents: 'none',
                        }}
                      >
                        <PhotoCamera sx={{ color: 'white', fontSize: { xs: 20, md: 24 } }} />
                      </Box>
                    )}
                  </Box>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="h3"
                      gutterBottom
                      sx={{
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        hyphens: 'auto',
                      }}
                    >
                      {profile.firstName} {profile.lastName}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Email sx={{ mr: 1, color: 'text.secondary', flexShrink: 0 }} />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          wordBreak: 'break-all',
                        }}
                      >
                        {profile.email}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Phone sx={{ mr: 1, color: 'text.secondary', flexShrink: 0 }} />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          wordWrap: 'break-word',
                          wordBreak: 'break-all',
                        }}
                      >
                        {profile.phoneNumber}
                      </Typography>
                    </Box>
                  </Box>
                </>
              ) : null}
            </Box>
            <Button
              variant="outlined"
              startIcon={<Edit />}
              size="small"
              sx={{
                borderRadius: 2,
                height: 32,
                mt: { xs: 2, md: 0 },
              }}
              onClick={handleOpenEdit}
            >
              Edit Profile
            </Button>
          </Box>
        </CardContent>
      </Card>

      <JobseekerProfileModal open={isEditOpen} onClose={handleCloseEdit} profile={profile} />
    </>
  );
};
